import { AccessHeadersHolder } from "@app/repositories/AccessHeadersHolder";
import { graphqlClientKoyeb } from "@app/repositories/clients";
import { graphql } from "@generated/client";
import {
    type AnswerUpsertInput,
    type CreateFileInput,
    type CreateQuestionsFromFileInput,
    type GetQuestionnaireOverviewDataFilter,
    type GetQuestionnaireOverviewDataInput,
    type QuestionCreateInput,
    type QuestionFilter,
    type QuestionnaireCreateInput,
    type QuestionnaireUpdateInput,
    type QuestionUpdateInput,
    type UpsertAiSuggestionInput,
} from "@generated/client/graphql";

const GetQuestionnaireHeaderDataQuery = graphql(`
    query GetQuestionnaireHeaderData($id: ID!) {
        questionnaire(id: $id) {
            id
            cmsId
            name
            deadline
            creatorId
            requesterEmail
            companyName
            questions {
                answer {
                    status
                }
            }
            locale {
                availableLocales
                default
            }
            updatedAt
        }
    }
`);

const GetQuestionnaireStatsQuery = graphql(`
    query GetQuestionnaireStats($id: ID!) {
        questionnaire(id: $id) {
            stats {
                completionStats {
                    title
                    dimensionKey
                    seriesKeys
                    dataRows {
                        completion
                        answered
                        validated
                        notAnswered
                    }
                    seriesConfig {
                        answered {
                            label
                            color
                        }
                        validated {
                            label
                            color
                        }
                        notAnswered {
                            label
                            color
                        }
                    }
                    chartMetadata {
                        totalQuestions
                    }
                }
                aiSuggestionStats {
                    title
                    dimensionKey
                    seriesKeys
                    dataRows {
                        completion
                        aiSuggestionSuccess
                        aiSuggestionFailed
                        aiSuggestionNotStarted
                    }
                    seriesConfig {
                        aiSuggestionSuccess {
                            label
                            color
                        }
                        aiSuggestionFailed {
                            label
                            color
                        }
                        aiSuggestionNotStarted {
                            label
                            color
                        }
                    }
                    chartMetadata {
                        totalQuestions
                    }
                }
                userActivityStats {
                    title
                    dimensionKey
                    seriesKeys
                    dataRows {
                        userId
                        validated
                        notValidated
                    }
                    seriesConfig {
                        validated {
                            label
                            color
                        }
                        notValidated {
                            label
                            color
                        }
                    }
                    chartMetadata {
                        userIds
                        totalQuestionsByIdx
                    }
                }
            }
        }
    }
`);

const GetQuestionnaireDetailsQuery = graphql(`
    query GetQuestionnaireDetails($id: ID!) {
        questionnaire(id: $id) {
            id
            cmsId
            name
            deadline
            requesterEmail
            companyName
            entityId
            locale {
                availableLocales
                default
            }
        }
    }
`);

const GetQuestionnaireGenerationStateQuery = graphql(`
    query GetQuestionnaireGenerationState($id: ID!) {
        questionnaire(id: $id) {
            isGenerating
        }
    }
`);

const GetQuestionnairesQuery = graphql(`
    query GetQuestionnaires {
        questionnaires {
            id
            cmsId
            name
            companyName
            requesterEmail
            deadline
            questions {
                answer {
                    status
                }
                ownerId
            }
            creatorId
            updatedAt
        }
    }
`);

const CreateExternalQuestionnaireQuery = graphql(`
    mutation CreateExternalQuestionnaire($input: QuestionnaireCreateInput!) {
        createQuestionnaire(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const UpdateQuestionnaireQuery = graphql(`
    mutation UpdateQuestionnaire($id: ID!, $input: QuestionnaireUpdateInput!) {
        updateQuestionnaire(id: $id, input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const CreateQuestionMutation = graphql(`
    mutation CreateQuestion($input: QuestionCreateInput!) {
        createQuestion(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const CreateQuestionsMutation = graphql(`
    mutation CreateQuestions($input: [QuestionCreateInput!]!) {
        createQuestions(input: $input) {
            status
        }
    }
`);

const CreateQuestionnaireFromTemplateMutation = graphql(`
    mutation CreateQuestionnaireFromTemplate(
        $templateId: ID!
        $input: QuestionnaireCreateInput!
    ) {
        createQuestionnaireFromTemplate(
            templateId: $templateId
            input: $input
        ) {
            status
            data {
                id
            }
        }
    }
`);

const GetQuestionnaireOverviewDataQuery = graphql(`
    query GetQuestionnaireOverviewData(
        $input: GetQuestionnaireOverviewDataInput!
    ) {
        getQuestionnaireOverviewData(input: $input) {
            questions {
                id
                officialId
                cmsId
                activation
                questionnaireId
                name
                description
                order
                type
                options
                type
                ownerId
                metadata {
                    dependsOn {
                        questionId
                        displayIfSelected
                    }
                }
                answer {
                    id
                    status
                    value
                    aiSuggestion {
                        success
                        value
                        isGenerating
                        documentCitations {
                            documentId
                            documentFileName
                            text
                        }
                    }
                    lastGeneratedAt
                    evidenceFiles {
                        id
                        name
                        signedUrl
                        mimeType
                        documentVersions {
                            document {
                                name
                            }
                        }
                    }
                }
                group {
                    id
                    name
                    order
                    level
                }
            }
        }
    }
`);

const GetQuestionsQuery = graphql(`
    query GetQuestions($filter: QuestionFilter!, $locale: String) {
        questions(filter: $filter, locale: $locale) {
            id
            officialId
            cmsId
            activation
            questionnaireId
            order
            name
            type
            options
            type
            description
            isRequired
            metadata {
                options {
                    name {
                        en
                        fr
                    }
                    proof
                }
                unit
                dependsOn {
                    questionId
                    displayIfSelected
                }
            }
            ownerId
            questionnaireId
            guidance
            guidanceExpertise
            answer {
                id
                value
                comment
                status
                lastGeneratedAt
                aiSuggestion {
                    documentCitations {
                        documentId
                        documentFileName
                        text
                    }
                    indicatorCitations {
                        indicatorId
                    }
                    motivation
                    success
                    value
                    comment
                    isGenerating
                }
                evidenceFiles {
                    id
                    name
                    signedUrl
                    mimeType
                    documentVersions {
                        document {
                            name
                        }
                    }
                }
            }
        }
    }
`);

const GetQuestionQuery = graphql(`
    query GetQuestion($id: ID!) {
        question(id: $id) {
            id
            activation
            isRequired
            answer {
                id
                value
                comment
                status
                aiSuggestion {
                    documentCitations {
                        documentId
                        documentFileName
                        text
                    }
                    indicatorCitations {
                        indicatorId
                    }
                    motivation
                    success
                    value
                    comment
                    isGenerating
                }
                evidenceFiles {
                    id
                    name
                    signedUrl
                    mimeType
                    documentVersions {
                        document {
                            name
                        }
                    }
                }
            }
            name
            nameMultiLocale {
                en
                fr
            }
            description
            descriptionMultiLocale {
                en
                fr
            }
            type
            metadata {
                options {
                    name {
                        en
                        fr
                    }
                    proof
                }
                unit
                dependsOn {
                    questionId
                    displayIfSelected
                }
                proof
                enableNaOption
            }
            ownerId
            questionnaireId
        }
    }
`);

const GetQuestionnairesDataPageQuery = graphql(`
    query GetQuestionnairesDataPage {
        frameworkQuestionnairesDataPage {
            questionnaireDataPages {
                id
                name
                cmsId
                description
                active
                assignment
                stats {
                    total
                    answered
                    aiSuggestionSuccess
                    validated
                    notAnswered
                }
                logoUrl
                faq {
                    question
                    answer
                }
                articleLink
                demoLink
                websiteLink
            }
        }
    }
`);

const UpdateQuestionMutation = graphql(`
    mutation UpdateQuestion($id: ID!, $input: QuestionUpdateInput!) {
        updateQuestion(id: $id, input: $input) {
            status
            error {
                message
            }
            data {
                id
            }
        }
    }
`);

const UpdateQuestionsMutation = graphql(`
    mutation UpdateQuestions($ids: [ID!]!, $input: QuestionUpdateInput!) {
        updateQuestions(ids: $ids, input: $input)
    }
`);

const UpsertAnswerMutation = graphql(`
    mutation UpsertAnswer($input: AnswerUpsertInput!) {
        upsertAnswer(input: $input) {
            id
        }
    }
`);

const DeleteQuestionMutation = graphql(`
    mutation DeleteQuestion($id: ID!) {
        deleteQuestion(id: $id) {
            status
            error {
                message
            }
            data {
                id
            }
        }
    }
`);

const DeleteQuestionsMutation = graphql(`
    mutation DeleteQuestions($ids: [ID!]!) {
        deleteQuestions(ids: $ids) {
            status
            error {
                message
            }
        }
    }
`);

const AcceptAiSuggestionsMutation = graphql(`
    mutation AcceptAiSuggestions($ids: [ID!]!) {
        acceptAiSuggestions(ids: $ids) {
            status
        }
    }
`);

const ValidateAnswersMutation = graphql(`
    mutation ValidateAnswers($ids: [ID!]!) {
        validateAnswers(ids: $ids) {
            status
        }
    }
`);
const GenerateQuestionAiSuggestionMutation = graphql(`
    mutation generateQuestionAiSuggestion($id: ID!) {
        generateQuestionAiSuggestion(id: $id) {
            status
            error {
                message
            }
        }
    }
`);

const RePromptQuestionAiSuggestionMutation = graphql(`
    mutation RePromptQuestionAiSuggestion(
        $id: ID!
        $action: String!
        $language: String
        $instructions: String
    ) {
        rePromptQuestionAiSuggestion(
            id: $id
            action: $action
            language: $language
            instructions: $instructions
        ) {
            documentCitations {
                documentId
                documentFileName
                text
            }
            indicatorCitations {
                indicatorId
            }
            motivation
            success
            value
            comment
            isGenerating
        }
    }
`);

const UpsertAiSuggestionMutation = graphql(`
    mutation UpsertAiSuggestion($id: ID!, $input: UpsertAiSuggestionInput!) {
        upsertAiSuggestion(id: $id, input: $input) {
            status
        }
    }
`);

const DeleteAiSuggestionMutation = graphql(`
    mutation DeleteAiSuggestion($id: ID!) {
        deleteAiSuggestion(id: $id) {
            status
            error {
                message
            }
        }
    }
`);

const DeleteAnswerMutation = graphql(`
    mutation DeleteAnswer($id: ID!) {
        deleteAnswer(id: $id) {
            id
        }
    }
`);

const ImportQuestionsFromFileMutation = graphql(`
    mutation ImportQuestionsFromFile(
        $questionnaireId: ID!
        $input: CreateQuestionsFromFileInput!
    ) {
        createQuestionsFromFile(
            questionnaireId: $questionnaireId
            input: $input
        ) {
            status
            error {
                message
            }
        }
    }
`);

export const DeleteExternalQuestionnaireMutation = graphql(`
    mutation DeleteExternalQuestionnaire($id: ID!) {
        deleteQuestionnaire(id: $id) {
            id
        }
    }
`);

const CreateFrameworkQuestionnaireMutation = graphql(`
    mutation CreateFrameworkQuestionnaire(
        $input: CreateFrameworkQuestionnaireInput!
    ) {
        createFrameworkQuestionnaire(input: $input) {
            status
            error {
                code
                message
                i18key
            }
            data {
                id
            }
        }
    }
`);

const CreateEvsQuestionnaireMutation = graphql(`
    mutation CreateEvsQuestionnaire($input: CreateEvsQuestionnaireInput!) {
        createEvsQuestionnaire(input: $input) {
            status
            error {
                code
                message
                i18key
            }
            data {
                id
            }
        }
    }
`);

const GenerateQuestionsAiSuggestionsMutation = graphql(`
    mutation GenerateQuestionsAiSuggestions($id: ID!) {
        generateQuestionsAiSuggestions(questionnaireId: $id) {
            status
            error {
                message
            }
        }
    }
`);

const GetQuestionnaireGroupsQuery = graphql(`
    query GetQuestionnaireGroups($id: ID!, $locale: String!) {
        questionnaire(id: $id) {
            groups(locale: $locale) {
                id
                name
                level
                shortName
            }
        }
    }
`);

const ExportQuestionnaireMutation = graphql(`
    mutation exportQuestionnaire($id: String!, $locale: String!) {
        exportQuestionnaire(id: $id, locale: $locale) {
            data {
                signedUrl
            }
        }
    }
`);

const ExportEvidenceFilesMutation = graphql(`
    mutation exportEvidenceFiles($id: String!) {
        exportEvidenceFiles(id: $id) {
            data {
                signedUrl
            }
        }
    }
`);

const GenerateFrameworkReportMutation = graphql(`
    mutation generateFrameworkReport(
        $frameworkUname: String!
        $locale: String!
    ) {
        generateFrameworkReport(
            frameworkUname: $frameworkUname
            locale: $locale
        ) {
            status
        }
    }
`);

const GetGeneratingQuestionnaireIdsQuery = graphql(`
    query GetGeneratingQuestionnaireIds {
        generatingQuestionnaireIds
    }
`);

const GetPopupQuestionsQuery = graphql(`
    query GetPopupQuestions($questionnaireIds: [ID!]!) {
        popupQuestions(questionnaireIds: $questionnaireIds) {
            id
            questionnaireId
            name
            answer {
                aiSuggestion {
                    isGenerating
                    success
                }
                lastGeneratedAt
            }
        }
    }
`);

export class GraphQLQuestionnaireRepository {
    getQuestionnaireHeaderData = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionnaireHeaderDataQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaireDetails = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionnaireDetailsQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaireGenerationState = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionnaireGenerationStateQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaireGroups = async (id: string, locale: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionnaireGroupsQuery,
            { id, locale },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaires = async () => {
        return graphqlClientKoyeb.request(
            GetQuestionnairesQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnairesDataPage = async () => {
        return graphqlClientKoyeb.request(
            GetQuestionnairesDataPageQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    };
    createExternalQuestionnaire = async (input: QuestionnaireCreateInput) => {
        return graphqlClientKoyeb.request(
            CreateExternalQuestionnaireQuery,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    updateQuestionnaire = async (
        id: string,
        input: QuestionnaireUpdateInput,
    ) => {
        return graphqlClientKoyeb.request(
            UpdateQuestionnaireQuery,
            { id, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createQuestion = async (input: QuestionCreateInput) => {
        return graphqlClientKoyeb.request(
            CreateQuestionMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createQuestions = async (input: QuestionCreateInput[]) => {
        return graphqlClientKoyeb.request(
            CreateQuestionsMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestion = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaireOverviewData = async (
        filter: GetQuestionnaireOverviewDataFilter,
        locale?: string,
    ) => {
        const input: GetQuestionnaireOverviewDataInput = {
            filter,
            locale: locale,
        };
        return graphqlClientKoyeb.request(
            GetQuestionnaireOverviewDataQuery,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestions = async (filter: QuestionFilter, locale?: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionsQuery,
            { filter, locale },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    updateQuestion = async (id: string, input: QuestionUpdateInput) => {
        return graphqlClientKoyeb.request(
            UpdateQuestionMutation,
            { id, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    updateQuestions = async (ids: string[], input: QuestionUpdateInput) => {
        return graphqlClientKoyeb.request(
            UpdateQuestionsMutation,
            { ids, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };
    upsertAnswer = async (input: AnswerUpsertInput) => {
        return graphqlClientKoyeb.request(
            UpsertAnswerMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    deleteQuestion = async (id: string) => {
        return graphqlClientKoyeb.request(
            DeleteQuestionMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    deleteQuestions = async (ids: string[]) => {
        return graphqlClientKoyeb.request(
            DeleteQuestionsMutation,
            { ids },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    acceptAiSuggestions = async (ids: string[]) => {
        return graphqlClientKoyeb.request(
            AcceptAiSuggestionsMutation,
            { ids },
            await AccessHeadersHolder.getHeaders(),
        );
    };


    validateAnswers = async (ids: string[]) => {
        return graphqlClientKoyeb.request(
            ValidateAnswersMutation,
            { ids },
            await AccessHeadersHolder.getHeaders(),
        );
    };
    generateAiSuggestion = async (questionId: string) => {
        return graphqlClientKoyeb.request(
            GenerateQuestionAiSuggestionMutation,
            { id: questionId },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    rePromptQuestionAiSuggestion = async (
        questionId: string,
        action: string,
        language?: string,
        instructions?: string,
    ) => {
        return graphqlClientKoyeb.request(
            RePromptQuestionAiSuggestionMutation,
            { id: questionId, action: action, language, instructions },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    upsertAiSuggestion = async (
        questionId: string,
        input: UpsertAiSuggestionInput,
    ) => {
        return graphqlClientKoyeb.request(
            UpsertAiSuggestionMutation,
            { id: questionId, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    deleteAiSuggestion = async (questionId: string) => {
        return graphqlClientKoyeb.request(
            DeleteAiSuggestionMutation,
            { id: questionId },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    deleteAnswer = async (id: string) => {
        return graphqlClientKoyeb.request(
            DeleteAnswerMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    importQuestionsFromFile = async (
        questionnaireId: string,
        input: CreateQuestionsFromFileInput,
    ) => {
        return graphqlClientKoyeb.request(
            ImportQuestionsFromFileMutation,
            { questionnaireId, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    deleteExternalQuestionnaire = async (id: string) => {
        return graphqlClientKoyeb.request(
            DeleteExternalQuestionnaireMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createFromTemplate = async (
        templateId: string,
        input: QuestionnaireCreateInput,
    ) => {
        return graphqlClientKoyeb.request(
            CreateQuestionnaireFromTemplateMutation,
            { templateId, input },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createFrameworkQuestionnaire = async (
        frameworkId: string,
        year: number,
        tagIds: string[],
        locale: string,
    ) => {
        return graphqlClientKoyeb.request(
            CreateFrameworkQuestionnaireMutation,
            { input: { frameworkCmsId: frameworkId, year, tagIds, locale } },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    createEvsQuestionnaire = async (
        year: number,
        s3File: CreateFileInput,
        locale: string,
        entityId: string,
    ) => {
        return graphqlClientKoyeb.request(
            CreateEvsQuestionnaireMutation,
            { input: { year, s3File, locale, entityId } },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    generateAiSuggestions = async (id: string) => {
        return graphqlClientKoyeb.request(
            GenerateQuestionsAiSuggestionsMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    export = async (id: string, locale: string) => {
        return graphqlClientKoyeb.request(
            ExportQuestionnaireMutation,
            { id, locale },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    exportEvidenceFiles = async (id: string) => {
        return graphqlClientKoyeb.request(
            ExportEvidenceFilesMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    generateFrameworkReport = async ({
        frameworkUname,
        locale,
    }: {
        frameworkUname: string;
        locale: string;
    }) => {
        return graphqlClientKoyeb.request(
            GenerateFrameworkReportMutation,
            { frameworkUname, locale },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getQuestionnaireStats = async (id: string) => {
        return graphqlClientKoyeb.request(
            GetQuestionnaireStatsQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getGeneratingQuestionnaireIds = async () => {
        return graphqlClientKoyeb.request(
            GetGeneratingQuestionnaireIdsQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    };

    getPopupQuestions = async (questionnaireIds: string[]) => {
        return graphqlClientKoyeb.request(
            GetPopupQuestionsQuery,
            { questionnaireIds },
            await AccessHeadersHolder.getHeaders(),
        );
    };
}
